import { useEffect, useMemo, useRef, useState } from "react";
import {
  Share2,
  Facebook,
  Link as LinkIcon,
  Check,
  MessageCircle,
  Mail,
  Smartphone,
  Linkedin,
  Smartphone as SmartphoneShareIcon,
} from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl, withUtm } from "../lib/social";

/**
 * Branded X (Twitter) monogram , Lucide doesn't ship the current one.
 */
const XIcon = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.97 6.817H1.674l7.73-8.836L1.254 2.25h6.828l4.713 6.231 5.45-6.231Zm-1.16 17.52h1.833L7.084 4.126H5.117l11.967 15.643Z" />
  </svg>
);

/**
 * Reddit monogram , Lucide doesn't ship one, so inline an SVG path.
 */
const RedditIcon = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm5.018 14.32c.024.16.036.32.036.484 0 2.474-2.88 4.477-6.43 4.477-3.55 0-6.43-2.003-6.43-4.477 0-.164.012-.324.036-.484-.624-.282-1.057-.91-1.057-1.639 0-.992.804-1.797 1.797-1.797.484 0 .922.193 1.245.505 1.215-.876 2.892-1.435 4.766-1.486l.91-4.293a.305.305 0 0 1 .363-.234l2.984.636a1.275 1.275 0 1 1-.13.598l-2.69-.573-.815 3.847c1.851.057 3.504.616 4.708 1.488a1.79 1.79 0 0 1 1.244-.5 1.797 1.797 0 0 1 .733 3.439ZM8.293 13.118a1.122 1.122 0 1 0 2.244 0 1.122 1.122 0 0 0-2.244 0Zm5.987 3.108c-.515.516-1.5.557-1.79.557h-.001c-.288 0-1.273-.04-1.788-.557a.196.196 0 0 1 .277-.277c.325.325 1.018.44 1.512.44.493 0 1.187-.115 1.512-.44a.196.196 0 0 1 .278.277Zm-.205-1.98a1.122 1.122 0 1 1 0-2.244 1.122 1.122 0 0 1 0 2.244Z" />
  </svg>
);

/**
 * "Share" trigger + popover that sits in the study modal footer to
 * the left of "Register your interest". On click, opens a small
 * popover with a "Know someone who'd benefit?" framing and the
 * platforms patients actually use to share study opportunities.
 *
 * Mobile devices that support the Web Share API get a "Share to your
 * apps…" option at the top, which surfaces the OS-native share sheet
 * (every app the user has installed , Messages, Mail, AirDrop,
 * Signal, etc.). Desktop falls back to the explicit list.
 *
 * Every share URL is UTM-tagged so PostHog can attribute candidates
 * back to (a) the modal placement, (b) the platform, (c) the study.
 */
export default function ShareInterestPopover({ study, audience = "patient" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  // Native Web Share API support , available on iOS Safari, Android
  // Chrome, and recent desktop browsers. Drives the "Share to your
  // apps…" button at the top of the popover when present.
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";
  // Coarse pointer (touch) heuristic , used to gate the SMS option,
  // which is only meaningful on mobile devices.
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(pointer: coarse)").matches;
  }, []);

  // Close on outside click + Escape
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!study) return null;

  const baseUrl = `https://www.meditalk.world/studies/${study.id}`;
  const text = `${study.title} , paid research on MediTalk`;
  const subject = `MediTalk study , ${study.condition || "research"}`;
  const campaign = `modal-share-${audience}-${study.id}`;

  const taggedUrl = (source) =>
    withUtm(baseUrl, { source, medium: "social", campaign });

  const items = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: buildShareUrl("whatsapp", {
        url: taggedUrl("share-whatsapp"),
        text,
      }),
    },
    // SMS is mobile-only , `sms:` opens a desktop dialog on macOS
    // sometimes, but the UX is awkward; we hide it on coarse-pointer
    // non-touch devices.
    isMobile && {
      id: "sms",
      label: "SMS / Messages",
      Icon: Smartphone,
      href: buildShareUrl("sms", { url: taggedUrl("share-sms"), text }),
    },
    {
      id: "email",
      label: "Email",
      Icon: Mail,
      href: buildShareUrl("email", {
        url: taggedUrl("share-email"),
        text,
        subject,
      }),
    },
    {
      id: "x",
      label: "X (Twitter)",
      Icon: XIcon,
      href: buildShareUrl("x", { url: taggedUrl("share-x"), text }),
    },
    {
      id: "facebook",
      label: "Facebook",
      Icon: Facebook,
      href: buildShareUrl("facebook", { url: taggedUrl("share-facebook") }),
    },
    {
      id: "reddit",
      label: "Reddit",
      Icon: RedditIcon,
      href: buildShareUrl("reddit", { url: taggedUrl("share-reddit"), text }),
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      Icon: Linkedin,
      href: buildShareUrl("linkedin", { url: taggedUrl("share-linkedin") }),
    },
  ].filter(Boolean);

  const onShareClick = () => {
    // Fire the "thanks" toast when an outbound share link is clicked.
    // Native intents don't give us a callback, so we react to the
    // click itself; close the popover after a short delay so the new
    // tab has time to open.
    toast.success("Thanks , that helps us reach more people who could benefit.");
    setTimeout(() => setOpen(false), 500);
  };

  const onNativeShare = async () => {
    try {
      await navigator.share({
        title: study.title,
        text,
        url: taggedUrl("share-native"),
      });
      toast.success("Thanks , that helps us reach more people who could benefit.");
      setOpen(false);
    } catch (err) {
      // User cancelled the OS share sheet , no error to surface.
      if (err && err.name !== "AbortError") {
        toast.error("Couldn't open the share sheet. Pick a specific option below.");
      }
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(taggedUrl("share-copy"));
      setCopied(true);
      toast.success("Thanks , that helps us reach more people who could benefit.");
      setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 1400);
    } catch {
      toast.error("Couldn't copy. Long-press the URL in your browser to copy manually.");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border text-sm font-semibold transition-colors ${
          open
            ? "border-[var(--teal)] text-[var(--teal-dark)] bg-[var(--teal-soft)]"
            : "border-[var(--light-border)] text-[var(--charcoal)] hover:border-[var(--teal)] hover:text-[var(--teal-dark)]"
        }`}
        data-testid="study-modal-share-trigger"
      >
        <Share2 size={16} />
        Share
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 bottom-full mb-2 w-72 z-30 bg-white rounded-2xl border border-[var(--light-border)] shadow-xl p-4 max-h-[70vh] overflow-y-auto"
          data-testid="study-modal-share-popover"
        >
          <p className="text-sm font-bold text-[var(--charcoal)] leading-snug mb-1">
            Know someone who'd benefit from this?
          </p>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed mb-3">
            Share this study , patients and caregivers often hear
            about research from a friend or family member first.
          </p>

          {canNativeShare && (
            <>
              <button
                type="button"
                onClick={onNativeShare}
                role="menuitem"
                data-testid="study-modal-share-native"
                className="w-full inline-flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 border-[var(--teal)] bg-[var(--teal-soft)] text-sm font-semibold text-[var(--teal-dark)] hover:bg-[var(--teal)]/15 transition-colors"
              >
                <SmartphoneShareIcon size={16} />
                Share to your apps…
              </button>
              <div className="my-3 border-t border-[var(--light-border)]" />
            </>
          )}

          <div className="grid grid-cols-1 gap-1.5">
            {items.map(({ id, label, Icon, href }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onShareClick}
                role="menuitem"
                data-testid={`study-modal-share-${id}`}
                className="inline-flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-[var(--light-border)] text-sm font-semibold text-[var(--charcoal)] hover:bg-[var(--bone-soft)] hover:border-[var(--teal)] hover:text-[var(--teal-dark)] transition-colors"
              >
                <Icon size={16} />
                {label}
              </a>
            ))}
            <button
              type="button"
              onClick={onCopy}
              role="menuitem"
              data-testid="study-modal-share-copy"
              className="inline-flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-[var(--light-border)] text-sm font-semibold text-[var(--charcoal)] hover:bg-[var(--bone-soft)] hover:border-[var(--teal)] hover:text-[var(--teal-dark)] transition-colors"
            >
              {copied ? (
                <Check size={16} strokeWidth={3} className="text-[var(--teal)]" />
              ) : (
                <LinkIcon size={16} />
              )}
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
